import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
    Shield, AlertTriangle, Zap, Radio, Globe, Heart, Lock, CheckCircle,
    Smartphone, Activity, TrendingUp, Users, Target, Rocket, DollarSign,
    MapPin, Bell, Brain, ChevronRight, ChevronLeft, ArrowRight, Play,
    Building, Award, Clock, Wifi, BarChart3, PieChart, Calendar,
    Phone, Mail, Linkedin, ExternalLink, Download, Star, Eye,
    Cpu, Server, Database, Layers, Signal,
    Box, Atom, Hash, Terminal, Network, Cloud,
    Settings, BarChart
} from 'lucide-react';
import HeroImage25D from '../components/website/HeroImage25D';

// ============================================================================
// PITCH DECK DATA (v2.1 - Master Vision)
// ============================================================================

const problemStats = [
    { value: '150K+', label: 'Displaced (Feb \'26 Floods)', color: '#EF4444', icon: Users },
    { value: '110K', label: 'Hectares Destroyed', color: '#F59E0B', icon: Activity },
    { value: '0s', label: 'Early Warning Given', color: '#64748B', icon: Clock }, // Slate for "Zero"
    { value: 'ZERO', label: 'Casualty Goal', color: '#10B981', icon: Target },
];

const painPoints = [
    { title: 'The Poly-Crisis', desc: 'February 2026 proved it: We face simultaneous floods (Ksar El Kbir) and drought. Traditional systems cannot handle this volatility.', icon: AlertTriangle },
    { title: 'Reactive = Fatal', desc: 'Sirens and cell broadcasts fail when telco towers flood. We need a grid that survives the collapse.', icon: Radio },
    { title: 'Data Sovereignty', desc: 'National data security is non-negotiable. We cannot rely on foreign cloud APIs for critical safety infrastructure.', icon: Lock },
    { title: 'Water Management', desc: 'Dams are overflowing because they lack predictive intelligence. We need active, AI-driven release protocols.', icon: TrendingUp },
];

const solutionFeatures = [
    { icon: Smartphone, title: 'Layer 1: The App', desc: 'Offline-first. Syncs location & health data via Bluetooth Mesh when cellular fails.', color: '#38BDF8' }, // Sky-400
    { icon: Wifi, title: 'Layer 2: The Router', desc: 'Sovereign Home Guardian. Hijacks DNS for alerts. Detects gas/quakes via sensors.', color: '#F59E0B' }, // Amber-500
    { icon: Globe, title: 'Layer 3: The Satellite', desc: 'Vision 2030. Direct uplink to Mohammed VI Satellites for unkillable coverage.', color: '#8B5CF6' }, // Violet-500
    { icon: Brain, title: 'Zelli Angel (The Core)', desc: 'Rust Core. Our Guardian AI. Predicts floods 48h in advance & triggers "Neural Reflexes" to save lives.', color: '#10B981' }, // Emerald-500
    { icon: Activity, title: 'Metronim Protocol', desc: 'Active Dam Management. Automated valve release based on upstream sensor data.', color: '#EC4899' }, // Pink-500
    { icon: Lock, title: 'Sovereign Grid', desc: 'Data never leaves Morocco. Local-first architecture (ElectricSQL) + On-premise training.', color: '#64748B' }, // Slate-500
];

const techSpecs = [
    { icon: Cpu, title: 'Rust Core', desc: 'Memory-safe, sub-50ms inference. The engine of the "Zero-Hardware" mesh.', color: '#F59E0B' },
    { icon: Radio, title: 'LoRaWAN + BLE', desc: 'Long-range low-power comms for red zone sensors (Metronim).', color: '#3B82F6' },
    { icon: Database, title: 'Local-First Sync', desc: 'ElectricSQL. Works 100% offline. Syncs instantly when connectivity returns.', color: '#10B981' },
    { icon: Server, title: 'Zero-Hardware Nodes', desc: 'Proprietary DNS script transforms 2M+ existing ISP routers into safety beacons.', color: '#EC4899' },
    { icon: Brain, title: 'Zelli Angel', desc: 'Candle.rs framework. Like a "Guardian Angel" in every pocket, processing threats offline.', color: '#8B5CF6' },
    { icon: Shield, title: 'Encryption At Rest', desc: 'SQLCipher + Sodium. Military-grade data protection for national security.', color: '#64748B' },
];

const marketData = {
    morocco: { population: '38.4M', smartphones: '30.7M', status: 'Launch' },
    africa: { population: '1.4B', penetration: 'Growing', status: 'Scale' },
    tam: '€4.2B', // MENA Safety Market
    sam: '€420M', // North Africa
    som: '€42M', // Morocco Year 1-2
};

const businessModel = [
    { stream: 'B2G: Sovereign License', target: 'Government (Interior/Water)', price: 'Annual Contract', potential: 'High Volume' },
    { stream: 'B2B: Enterprise Safety', target: 'Banks, OCP, Mines', price: 'Per Seat / Year', potential: 'Recurring ARR' },
    { stream: 'B2C: Family Premium', target: 'Upper/Middle Class', price: '20 MAD / Month', potential: 'Mass Scale' },
    { stream: 'Telecom Partnerships', target: 'Inwi / IAM', price: 'Rev-Share / Bundle', potential: 'Strategic' },
];

const roadmap = [
    { phase: 'Q1 2026', title: 'The Software Grid', items: ['App Launch (iOS/Android)', 'Bluetooth Mesh v1', 'Ksar El Kbir Pilot Prep'], status: 'current' },
    { phase: 'Q2 2026', title: 'The Router Hack', items: ['ISP Router DNS Patch', 'Sovereign Home Guardian', 'Telecom Deal'], status: 'upcoming' },
    { phase: 'Q3 2026', title: 'Metronim Sentinel', items: ['200 Hardware Nodes', 'Dam SCADA Integration', 'Red Zone Deployment'], status: 'upcoming' },
    { phase: '2027+', title: 'National Scale', items: ['1M Active Nodes', 'Sovereign AI Cluster', 'Export to MENA'], status: 'upcoming' },
];

const fundingAllocation = [
    { category: 'R&D (Rust/AI)', amount: '8M MAD', percentage: 40, desc: 'Edge AI models, Mesh networking core' },
    { category: 'Hardware (Metronim)', amount: '6M MAD', percentage: 30, desc: 'Sensor production & deployment' },
    { category: 'Growth & Ops', amount: '4M MAD', percentage: 20, desc: 'Marketing, Legal, Office' },
    { category: 'Contingency', amount: '2M MAD', percentage: 10, desc: 'Buffer' },
];

const traction = [
    { metric: 'Tech Readiness', value: 'TRL 7', icon: Settings, color: '#10B981' }, // Technical Readiness Level
    { metric: 'Pilot Site', value: 'Ksar El Kbir', icon: MapPin, color: '#F59E0B' },
    { metric: 'Gov Interest', value: 'High', icon: Building, color: '#3B82F6' },
    { metric: 'Waitlist', value: '15,000+', icon: Users, color: '#8B5CF6' },
];

// ============================================================================
// SLIDE WRAPPER (DESIGN SYSTEM)
// ============================================================================

const SlideWrapper: React.FC<{ children: React.ReactNode; id: string; className?: string }> = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            ref={ref}
            id={id}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className={`min-h-screen py-20 md:py-32 relative overflow-hidden border-b border-white/5 ${className}`}
        >
            {/* Technical Grid Overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

            {children}
        </motion.section>
    );
};

// ============================================================================
// COMPONENT: PITCH DECK PAGE
// ============================================================================

const PitchDeckPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-amber-500/30 selection:text-amber-100">

            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 px-6 py-4 bg-slate-950/90 backdrop-blur-md border-b border-white/5 flex justify-between items-center">
                <div onClick={() => window.location.href = '/'} className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-8 h-8 bg-amber-500 rounded flex items-center justify-center font-bold text-slate-950 text-xs">N</div>
                    <span className="text-sm font-bold tracking-widest text-slate-100 group-hover:text-amber-500 transition-colors">NAJA AI</span>
                </div>
                <div className="hidden md:flex gap-8 text-xs font-mono tracking-widest text-slate-500">
                    {['PROBLEM', 'SOLUTION', 'TECH', 'ROADMAP', 'ASK'].map((item) => (
                        <a key={item} href={`#${item.toLowerCase()}`} className="hover:text-amber-400 transition-colors">{item}</a>
                    ))}
                </div>
                <a href="mailto:invest@lanaja.ai" className="px-4 py-2 border border-amber-500/50 text-amber-400 text-xs font-mono font-bold rounded hover:bg-amber-500/10 transition-all">
                    INVESTOR LOGIN
                </a>
            </nav>

            {/* ================================================================ */}
            {/* SLIDE 1: TITLE / MISSION */}
            {/* ================================================================ */}
            <section className="relative min-h-screen flex flex-col items-center justify-center bg-slate-950 pt-20">
                <div className="text-center max-w-4xl px-4 z-10">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 mb-8 border border-white/10 rounded-full bg-white/5 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                            <span className="text-[10px] font-mono tracking-widest text-slate-300">CONFIDENTIAL // SEED ROUND</span>
                        </div>

                        <h1 className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tight mb-6 text-white leading-none">
                            MOROCCO'S <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-600">NEURAL LINK</span>
                        </h1>

                        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
                            Connecting 38M Citizens, Devices, and Routers into a Single Living Organism. <br />
                            <span className="text-white font-medium">Zelli is the Angel. The Network is the Shield.</span>
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="#problem" className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold tracking-wider rounded transition-all">
                                MISSION BRIEF
                            </a>
                        </div>
                    </motion.div>
                </div>

                {/* Background Grid */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.05)_0%,transparent_70%)] pointer-events-none" />
            </section>

            {/* ================================================================ */}
            {/* SLIDE 2: THE POLY-CRISIS */}
            {/* ================================================================ */}
            <SlideWrapper id="problem" className="bg-slate-950">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <span className="text-amber-500 font-mono text-xs tracking-widest mb-4 block">01 // THE REALITY</span>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                February 2026: <br />The <span className="text-red-500">Wake Up Call</span>.
                            </h2>
                            <p className="text-lg text-slate-400 leading-relaxed mb-8">
                                The Ksar El Kbir floods weren't a freak accident. They are the new normal.
                                Combined with the Al Haouz earthquake and chronic drought, Morocco faces a
                                <strong> "Poly-Crisis"</strong> that legacy infrastructure cannot handle.
                            </p>

                            <div className="grid grid-cols-2 gap-4">
                                {problemStats.map((stat, i) => (
                                    <div key={i} className="p-4 border border-slate-800 bg-slate-900/50 rounded-lg">
                                        <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} />
                                        <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                                        <div className="text-xs text-slate-500 font-mono">{stat.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            {painPoints.map((point, i) => (
                                <div key={i} className="p-6 border border-slate-800 bg-slate-900/30 rounded-lg hover:border-amber-500/30 transition-colors">
                                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                                        <point.icon className="w-4 h-4 text-amber-500" />
                                        {point.title}
                                    </h3>
                                    <p className="text-sm text-slate-400">{point.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </SlideWrapper>

            {/* ================================================================ */}
            {/* SLIDE 3: THE SOLUTION (LAYERS) */}
            {/* ================================================================ */}
            <SlideWrapper id="solution" className="bg-slate-950">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <span className="text-amber-500 font-mono text-xs tracking-widest mb-4 block">02 // THE STRATEGY</span>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">The Sovereign <span className="text-amber-500">Neural Link</span></h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            We don't just "alert". We create a <strong>National Reflex</strong>. <br />
                            When the earth shakes, the grid reacts before you even feel it. <br />
                            <span className="text-white font-bold">Zelli (The Angel)</span> orchestrates safety across 3 layers.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {solutionFeatures.map((feat, i) => (
                            <div key={i} className="group p-6 border border-slate-800 bg-slate-900/50 rounded-xl hover:border-amber-500/50 transition-all">
                                <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center mb-4 group-hover:bg-slate-700 transition-colors">
                                    <feat.icon className="w-6 h-6" style={{ color: feat.color }} />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{feat.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </SlideWrapper>

            {/* ================================================================ */}
            {/* SLIDE 4: TECHNOLOGY (RUST + AI) */}
            {/* ================================================================ */}
            <SlideWrapper id="tech" className="bg-slate-950">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div className="order-2 md:order-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {techSpecs.map((spec, i) => (
                                <div key={i} className="p-4 border border-slate-800 bg-slate-900/50 rounded-lg">
                                    <spec.icon className="w-5 h-5 mb-3" style={{ color: spec.color }} />
                                    <h4 className="font-bold text-white text-sm mb-1">{spec.title}</h4>
                                    <p className="text-xs text-slate-500 leading-relaxed">{spec.desc}</p>
                                </div>
                            ))}
                        </div>

                        <div className="order-1 md:order-2">
                            <span className="text-amber-500 font-mono text-xs tracking-widest mb-4 block">03 // THE ENGINE</span>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Built fo <span className="text-amber-500">Sovereignty</span>.
                            </h2>
                            <p className="text-lg text-slate-400 leading-relaxed mb-6">
                                We chose <strong>Rust</strong> for safety and speed. We chose <strong>ElectricSQL</strong> for offline-first resilience.
                                We chose <strong>Local AI</strong> because in a crisis, the cloud is just someone else's broken computer.
                            </p>
                            <div className="flex items-center gap-4 text-xs font-mono text-slate-500">
                                <span className="px-3 py-1 border border-slate-800 rounded">RUST 1.8</span>
                                <span className="px-3 py-1 border border-slate-800 rounded">TAURI 2.0</span>
                                <span className="px-3 py-1 border border-slate-800 rounded">WASM</span>
                            </div>
                        </div>
                    </div>
                </div>
            </SlideWrapper>

            {/* ================================================================ */}
            {/* SLIDE 5: ROADMAP */}
            {/* ================================================================ */}
            <SlideWrapper id="roadmap" className="bg-slate-950">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <span className="text-amber-500 font-mono text-xs tracking-widest mb-4 block">04 // EXECUTION PLAN</span>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">From Code to Satellites</h2>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {roadmap.map((phase, i) => (
                            <div key={i} className={`relative p-6 border rounded-xl ${phase.status === 'current' ? 'border-amber-500 bg-amber-500/5' : 'border-slate-800 bg-slate-900/30'}`}>
                                <div className="text-xs font-mono font-bold text-slate-500 mb-2">{phase.phase}</div>
                                <h3 className={`text-lg font-bold mb-4 ${phase.status === 'current' ? 'text-amber-400' : 'text-white'}`}>{phase.title}</h3>
                                <ul className="space-y-2">
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="text-xs text-slate-400 flex items-center gap-2">
                                            <div className={`w-1 h-1 rounded-full ${phase.status === 'current' ? 'bg-amber-500' : 'bg-slate-600'}`} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </SlideWrapper>

            {/* ================================================================ */}
            {/* SLIDE 6: THE ASK */}
            {/* ================================================================ */}
            <SlideWrapper id="ask" className="bg-slate-950">
                <div className="container mx-auto px-6 max-w-4xl text-center">
                    <span className="text-green-500 font-mono text-xs tracking-widest mb-4 block">05 // THE ASK</span>
                    <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
                        20,000,000 <span className="text-slate-600">MAD</span>
                    </h2>
                    <p className="text-xl text-slate-400 mb-12">
                        Seed Round to deploy the Ksar El Kbir pilot, finalize the Router Guardian protocol, and secure the first government contracts.
                    </p>

                    <div className="grid md:grid-cols-4 gap-4 mb-12">
                        {fundingAllocation.map((item, i) => (
                            <div key={i} className="p-4 border border-slate-800 bg-slate-900/50 rounded-lg text-left">
                                <div className="text-green-500 font-bold text-lg mb-1">{item.percentage}%</div>
                                <div className="text-white font-bold text-sm mb-1">{item.category}</div>
                                <div className="text-xs text-slate-500">{item.amount}</div>
                            </div>
                        ))}
                    </div>

                    <a href="mailto:aymane.ldev@gmail.com" className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-slate-950 font-bold text-lg rounded transition-all">
                        <Mail className="w-5 h-5" />
                        CONTACT FOUNDER
                    </a>
                </div>
            </SlideWrapper>

            {/* ================================================================ */}
            {/* SLIDE 7: FOUNDER & SPECIALIST */}
            {/* ================================================================ */}
            <SlideWrapper id="founder" className="bg-slate-950">
                <div className="container mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative inline-block"
                    >
                        {/* 2.5D Founder Image */}
                        <div className="relative z-10 w-64 h-64 md:w-80 md:h-80 mx-auto">
                            <HeroImage25D
                                src="/team/founder.png"
                                alt="Aymane El Amrani"
                                className="w-full h-full object-contain drop-shadow-2xl"
                            />

                            {/* Floating Tech Icons (Behind) */}
                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute -top-4 -left-12 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-[#61DAFB] shadow-lg backdrop-blur-sm"
                            >
                                <Atom className="w-8 h-8" /> {/* React */}
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -15, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                                className="absolute top-1/2 -right-16 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-[#F74C00] shadow-lg backdrop-blur-sm"
                            >
                                <Settings className="w-8 h-8" /> {/* Rust Proxy */}
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -12, 0] }}
                                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                className="absolute -bottom-4 -left-8 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white shadow-lg backdrop-blur-sm"
                            >
                                <Box className="w-8 h-8" /> {/* Unity 3D */}
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -14, 0] }}
                                transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                                className="absolute bottom-1/3 -left-20 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-[#3776AB] shadow-lg backdrop-blur-sm"
                            >
                                <Terminal className="w-8 h-8" /> {/* Python */}
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -11, 0] }}
                                transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                                className="absolute -top-8 right-0 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-[#178600] shadow-lg backdrop-blur-sm"
                            >
                                <Hash className="w-8 h-8" /> {/* C# */}
                            </motion.div>

                            <motion.div
                                animate={{ y: [0, -13, 0] }}
                                transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                                className="absolute bottom-0 -right-12 z-0 p-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-amber-500 shadow-lg backdrop-blur-sm"
                            >
                                <Network className="w-8 h-8" /> {/* Networking */}
                            </motion.div>
                        </div>

                        {/* Name & Title */}
                        <div className="mt-12">
                            <h3 className="text-3xl font-bold text-white mb-2">Aymane El Amrani</h3>
                            <p className="text-amber-500 font-mono tracking-widest uppercase mb-6">Founder & CTO</p>
                            <div className="inline-flex gap-2">
                                <span className="px-3 py-1 bg-slate-900 border border-slate-800 rounded text-xs text-slate-400 font-mono">Systems Architect</span>
                                <span className="px-3 py-1 bg-slate-900 border border-slate-800 rounded text-xs text-slate-400 font-mono">Full Stack Engineer</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </SlideWrapper>
            {/* FOOTER */}
            <footer className="py-8 bg-slate-950 border-t border-white/5 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="w-6 h-6 bg-slate-800 rounded flex items-center justify-center font-bold text-slate-400 text-[10px]">N</div>
                    <span className="text-slate-500 font-bold tracking-widest text-xs">NAJA INTELLIGENCE SYSTEMS</span>
                </div>
                <p className="text-xs text-slate-700 font-mono">
                    CONFIDENTIAL & PROPRIETARY. DO NOT DISTRIBUTE.
                    <br />
                    RABAT • CHEFCHAOUEN • BENGUERIR
                </p>
            </footer>

        </div>
    );
};

export default PitchDeckPage;
